import { ApiError, normalizeApiError } from "./errors";

type GetToken = () => Promise<string | null>;
interface RequestOptions extends RequestInit { timeoutMs?: number; retry?: boolean; responseType?: "json" | "auto" }

export class ApiClient {
  private readonly baseUrls: string[];

  constructor(baseUrl: string | readonly string[], private getToken: GetToken, private scheme = "Bearer") {
    this.baseUrls = typeof baseUrl === "string" ? [baseUrl] : [...baseUrl];
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = await this.getToken();
    if (!token) throw new ApiError("not_authenticated", 401, "Authentication required.");

    let lastNetworkError: unknown;
    for (const [index, baseUrl] of this.baseUrls.entries()) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 12000);
      try {
        const response = await fetch(`${baseUrl}${path}`, {
          ...options, signal: options.signal ?? controller.signal,
          headers: { Accept: "application/json", ...(options.body ? { "Content-Type": "application/json" } : {}), ...options.headers, Authorization: `${this.scheme} ${token}` }
        });
        const requestId = response.headers.get("x-request-id") ?? undefined;
        if (response.status === 204 || response.status === 205) return undefined as T;
        const contentType = response.headers.get("content-type") ?? "";
        const isJson = contentType.includes("application/json");
        const body = isJson ? await response.json() as unknown : options.responseType === "auto" ? await response.text() : null;
        if (!response.ok) {
          const record = body && typeof body === "object" ? body as Record<string, unknown> : {};
          const nested = record.error && typeof record.error === "object" ? record.error as Record<string, unknown> : {};
          const code = typeof nested.code === "string" ? nested.code : typeof record.code === "string" ? record.code : typeof record.error === "string" ? record.error : `http_${response.status}`;
          const message = typeof nested.message === "string" ? nested.message : typeof record.message === "string" ? record.message : typeof record.error === "string" ? record.error : "The request failed.";
          throw new ApiError(code, response.status, message, requestId, response.status === 408 || response.status === 429 || response.status >= 500);
        }
        if (!isJson && options.responseType !== "auto") throw new ApiError("invalid_response", 502, "The service returned an invalid response.", requestId);
        return body as T;
      } catch (error) {
        if (error instanceof ApiError || index === this.baseUrls.length - 1 || options.signal?.aborted) throw normalizeApiError(error);
        lastNetworkError = error;
      } finally {
        clearTimeout(timeout);
      }
    }

    throw normalizeApiError(lastNetworkError);
  }
}
